import React, { useState } from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import creditCardsData from '../../data/creditCards.json';

const CreditCardComparison = () => {
  const [creditCards, setCreditCards] = useState(creditCardsData);
  const [sortBy, setSortBy] = useState('rating');
  const [filterBy, setFilterBy] = useState('all');

  const handleSort = (criteria) => {
    setSortBy(criteria);
    const sorted = [...creditCards].sort((a, b) => {
      switch (criteria) {
        case 'rating':
          return b.rating - a.rating;
        case 'annualFee':
          return a.annualFee - b.annualFee;
        case 'cashback':
          return b.cashback.localeCompare(a.cashback);
        default:
          return 0;
      }
    });
    setCreditCards(sorted);
  };

  const handleFilter = (filter) => {
    setFilterBy(filter);
    if (filter === 'all') {
      setCreditCards(creditCardsData);
    } else if (filter === 'noAnnualFee') {
      setCreditCards(creditCardsData.filter(card => card.annualFee === 0));
    } else if (filter === 'highCashback') {
      setCreditCards(creditCardsData.filter(card => card.cashback.includes('5%')));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Best Credit Cards Comparison 2025"
        description="Compare best credit cards with rewards, cashback, and benefits. Find the perfect credit card for your spending needs and lifestyle. Compare credit cards in India."
        keywords="best credit cards, credit card comparison, cashback cards, reward cards, credit card benefits, credit cards India, best credit card 2025"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Comparisons', url: '/comparisons' },
          { name: 'Credit Card Comparison', url: '/comparisons/credit-cards' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Credit Card Comparison', url: '/comparisons/credit-cards' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Best Credit Cards Comparison 2025
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Compare the best credit cards with rewards, cashback, and benefits. Find the perfect card for your spending needs.
          </p>
        </div>

        {/* Filters and Sort */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by:</label>
              <select
                value={filterBy}
                onChange={(e) => handleFilter(e.target.value)}
                className="input-field"
              >
                <option value="all">All Cards</option>
                <option value="noAnnualFee">No Annual Fee</option>
                <option value="highCashback">High Cashback</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => handleSort(e.target.value)}
                className="input-field"
              >
                <option value="rating">Rating</option>
                <option value="annualFee">Annual Fee</option>
                <option value="cashback">Cashback</option>
              </select>
            </div>
          </div>
        </div>

        {/* Credit Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {creditCards.map((card) => (
            <div key={card.id} className="card hover:shadow-xl transition-all duration-300">
              {/* Card Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💳</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{card.name}</h3>
                    <p className="text-sm text-gray-600">{card.bank}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-yellow-400">★</span>
                  <span className="text-sm font-medium text-gray-700">{card.rating}</span>
                </div>
              </div>

              {/* Key Features */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Annual Fee</span>
                  <span className="font-semibold text-gray-900">
                    {card.annualFee === 0 ? 'Free' : formatCurrency(card.annualFee)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Interest Rate</span>
                  <span className="font-semibold text-gray-900">{card.interestRate}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Cashback</span>
                  <span className="font-semibold text-green-600">{card.cashback}</span>
                </div>
              </div>

              {/* Features List */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {card.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Eligibility */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Eligibility:</h4>
                <p className="text-sm text-gray-600">{card.eligibility}</p>
              </div>

              {/* Action Button */}
              <button className="w-full btn-primary">
                Apply Now
              </button>
            </div>
          ))}
        </div>

        {/* Educational Content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              💡 Credit Card Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Pay your bills on time to avoid interest</li>
              <li>• Use cashback cards for maximum benefits</li>
              <li>• Keep credit utilization below 30%</li>
              <li>• Monitor your credit score regularly</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🏆 Best Practices
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Choose cards based on your spending pattern</li>
              <li>• Look for cards with no annual fees</li>
              <li>• Compare reward rates before applying</li>
              <li>• Read terms and conditions carefully</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📊 Credit Score Impact
            </h3>
            <p className="text-sm text-gray-600">
              Credit cards can help build your credit score when used responsibly. 
              Make timely payments and keep balances low to maintain a good credit history.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CreditCardComparison;
